import { useRef, useEffect, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Badge } from '../../../../../components/ui/badge';
import { motion } from 'framer-motion';
import { useExpandable } from '../../../../../hooks/use-expandable';
import { useImageUpload } from '../../../../../hooks/use-image-upload';
import { Music, ChevronDown, ChevronUp, Upload, Trash2, Loader2, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { storageService } from '../../../services/storageService';
import { aiProcessingService } from '../../../services/aiProcessingService';
import { messagesApi } from '../../../services/messagesApi';
import { toast } from '@/hooks/use-toast';
import { AddNodeMenu } from './AddNodeMenu';

interface AudioMessageData {
  label?: string;
  audioUrl?: string;
  fileName?: string;
  messageId?: string;
  aiTranscription?: string;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'error';
  flowId?: string;
  groupId?: string;
  sequenceOrder?: number;
}

export function AudioMessageNode({ id, data, isConnectable }: NodeProps<AudioMessageData>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { isExpanded, toggleExpand, animatedHeight } = useExpandable(false);
  const { setNodes, getNodes } = useReactFlow();
  const [aiTranscription, setAiTranscription] = useState(data.aiTranscription || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<'pending' | 'processing' | 'completed' | 'error'>(
    data.processingStatus || 'pending'
  );

  const {
    previewUrl,
    fileName,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
  } = useImageUpload({
    onUpload: (url) => console.log("Audio cargado:", url),
    initialUrl: data.audioUrl,
    initialFileName: data.fileName,
  });

  useEffect(() => {
    if (contentRef.current) {
      animatedHeight.set(isExpanded ? contentRef.current.offsetHeight : 0);
    }
  }, [isExpanded, animatedHeight, previewUrl]);

  const handleSave = async () => {
    // Permitir actualizar si ya existe un messageId y audioUrl
    if (!previewUrl && !data.audioUrl) {
      toast.error('Debes seleccionar un audio primero');
      return;
    }

    setIsSaving(true);
    setIsProcessing(true);
    setProcessingStatus('processing');

    try {
      // Obtener valores actualizados del nodo
      const currentNodes = getNodes();
      const currentNode = currentNodes.find(n => n.id === id);

      if (!currentNode) {
        throw new Error('Nodo no encontrado');
      }

      let finalAudioUrl = data.audioUrl;
      let finalFileName = fileName;

      // 1. Solo subir si es un nuevo audio (blob URL) o si no hay audioUrl previa
      if (previewUrl && previewUrl.startsWith('blob:') && fileInputRef.current?.files?.[0]) {
        const file = fileInputRef.current.files[0];
        const uploadResult = await storageService.uploadFile(file, 'audio');
        finalAudioUrl = uploadResult.url;
        finalFileName = uploadResult.fileName;

        toast.info('Audio subido, transcribiendo con IA...');
      }

      // 2. Transcribir con IA solo si es un audio nuevo
      let transcription = data.aiTranscription || '';
      if (previewUrl && previewUrl.startsWith('blob:') && finalAudioUrl) {
        transcription = await aiProcessingService.processAudioTranscription(
          finalAudioUrl,
          finalFileName!
        );
        setAiTranscription(transcription);
      }

      setProcessingStatus('completed');

      // 3. Guardar en BD
      const savedMessage = await messagesApi.upsertMessage({
        id: data.messageId,
        node_id: id,
        node_type: 'audio',
        media_url: finalAudioUrl!,
        file_name: finalFileName!,
        ai_description: transcription,
        ai_processing_status: 'completed',
        mime_type: fileInputRef.current?.files?.[0]?.type || 'audio/mpeg',
        file_size: fileInputRef.current?.files?.[0]?.size || 0,
        flow_id: currentNode.data.flowId || null,
        group_id: currentNode.data.groupId || null,
        sequence_order: currentNode.data.sequenceOrder || 1,
      });

      // 4. Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                audioUrl: finalAudioUrl,
                fileName: finalFileName,
                messageId: savedMessage.id,
                aiTranscription: transcription,
                processingStatus: 'completed'
              }
            }
            : node
        )
      );

      toast.success(data.messageId ? 'Audio actualizado y transcrito' : 'Audio guardado y transcrito correctamente');
    } catch (error) {
      console.error('Error:', error);
      setProcessingStatus('error');
      toast.error('Error al guardar el audio');
    } finally {
      setIsSaving(false);
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!data.messageId) {
      toast.error('No hay mensaje guardado para eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este audio de la base de datos?')) {
      return;
    }

    try {
      await messagesApi.deleteMessage(id);

      // Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                messageId: undefined,
                aiTranscription: undefined,
                processingStatus: 'pending'
              }
            }
            : node
        )
      );

      toast.success('Audio eliminado de la base de datos');
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
      toast.error('Error al eliminar el audio');
    }
  };

  const getStatusBadge = () => {
    if (isSaving) {
      return (
        <Badge className="bg-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" />
        </Badge>
      );
    }
    if (processingStatus === 'completed' && data.messageId) {
      return (
        <Badge className="bg-green-500">
          <CheckCircle className="h-3 w-3" />
        </Badge>
      );
    }
    if (processingStatus === 'error') {
      return (
        <Badge className="bg-red-500">
          <AlertTriangle className="h-3 w-3" />
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500">
        <AlertCircle className="h-3 w-3" />
      </Badge>
    );
  };

  return (
    <Card className="min-w-[300px] max-w-[400px] bg-white shadow-lg border-2 border-purple-200">
      {/* Número de secuencia - arriba izquierda */}
      {data.groupId && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white">
            {data.sequenceOrder || 1}
          </div>
        </div>
      )}

      <div className="absolute -top-2 -right-2 z-10">
        {getStatusBadge()}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-purple-500 hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />

      <div className="p-4">
        <div className="flex justify-between items-center gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-purple-500" />
            <h3 className="font-medium text-foreground">Enviar Audio</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpand}
            className="h-8 w-8 p-0"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        <motion.div
          style={{ height: animatedHeight }}
          className="overflow-hidden"
        >
          <div ref={contentRef} className="space-y-3 pt-2">
            <Input
              type="file"
              accept="audio/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
            />

            {!previewUrl ? (
              <div
                onClick={handleThumbnailClick}
                className={cn(
                  "flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 transition-colors hover:bg-purple-100"
                )}
              >
                <div className="rounded-full bg-white p-3 shadow-sm">
                  <Music className="h-6 w-6 text-purple-400" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Seleccionar audio</p>
                  <p className="text-xs text-gray-500">MP3, WAV, OGG, M4A</p>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-purple-100 p-3">
                      <Music className="h-6 w-6 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {fileName}
                      </p>
                      <p className="text-xs text-gray-500">Archivo de audio</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleThumbnailClick}
                        className="h-8 w-8 p-0"
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleRemove}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {previewUrl && (
                    <audio controls className="w-full mt-3">
                      <source src={previewUrl} />
                    </audio>
                  )}
                </div>
              </div>
            )}

            {aiTranscription && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-xs font-semibold text-blue-700 mb-1 block">
                  Transcripción generada por IA:
                </label>
                <p className="text-xs text-gray-700">
                  {aiTranscription}
                </p>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              {data.messageId && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 w-7 p-0"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={!previewUrl || isSaving}
                onClick={handleSave}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    {isProcessing ? 'Procesando...' : 'Guardando...'}
                  </>
                ) : (
                  data.messageId ? 'Actualizar' : 'Guardar'
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {!isExpanded && previewUrl && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Music className="h-3 w-3" />
              <span className="truncate">{fileName || 'Audio cargado'}</span>
            </div>
            {aiTranscription && (
              <div className="text-xs text-blue-600 truncate">
                ✓ Transcrito por IA
              </div>
            )}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-purple-500 hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />
      <AddNodeMenu nodeId={id} />
    </Card>
  );
}
